import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApiHost,
  getApiHostFromUrl,
  is503Error,
  isKilnStatusApiUrl,
  isLikelyConnectivityError,
} from './api-connectivity.util';

const KILN_STATUS_503_THRESHOLD = 5;

@Injectable({ providedIn: 'root' })
export class ApiConnectivityService {
  private kilnReachableSubject = new BehaviorSubject<boolean>(true);
  private claimReachableSubject = new BehaviorSubject<boolean>(true);
  private kilnStatus503Streak = 0;

  readonly kilnReachable$ = this.kilnReachableSubject.asObservable();
  readonly claimReachable$ = this.claimReachableSubject.asObservable();

  isLikelyConnectivityError(error: unknown): boolean {
    return isLikelyConnectivityError(error);
  }

  recordSuccess(url: string): void {
    if (isKilnStatusApiUrl(url)) {
      this.kilnStatus503Streak = 0;
    }
    const host = getApiHostFromUrl(url);
    if (host === 'kiln') {
      this.kilnReachableSubject.next(true);
    } else if (host === 'claim') {
      this.claimReachableSubject.next(true);
    }
  }

  recordFailure(url: string, error: unknown): void {
    if (!isLikelyConnectivityError(error)) {
      return;
    }
    const host = getApiHostFromUrl(url);
    if (host === 'kiln') {
      if (is503Error(error) && isKilnStatusApiUrl(url)) {
        this.kilnStatus503Streak++;
        if (this.kilnStatus503Streak >= KILN_STATUS_503_THRESHOLD) {
          this.kilnReachableSubject.next(false);
        }
        return;
      }
      this.kilnStatus503Streak = 0;
      this.kilnReachableSubject.next(false);
    } else if (host === 'claim') {
      this.claimReachableSubject.next(false);
    }
  }

  getUnreachableHosts$(hosts: ApiHost[] = ['kiln', 'claim']): Observable<ApiHost[]> {
    return combineLatest([this.kilnReachable$, this.claimReachable$]).pipe(
      map(([kilnReachable, claimReachable]) => {
        const unreachable: ApiHost[] = [];
        if (hosts.includes('kiln') && !kilnReachable) {
          unreachable.push('kiln');
        }
        if (hosts.includes('claim') && !claimReachable) {
          unreachable.push('claim');
        }
        return unreachable;
      })
    );
  }

  getIssueMessageKey(unreachableHosts: ApiHost[]): string {
    if (unreachableHosts.includes('kiln') && unreachableHosts.includes('claim')) {
      return 'CONNECTIVITY.BOTH_UNREACHABLE';
    }
    if (unreachableHosts.includes('kiln')) {
      return 'CONNECTIVITY.KILN_UNREACHABLE';
    }
    if (unreachableHosts.includes('claim')) {
      return 'CONNECTIVITY.CLAIM_UNREACHABLE';
    }
    return '';
  }
}
